// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export type ProposalType = 'PENSION' | 'FUND_INVESTMENT' | 'PROTECTION' | 'BREAKTHROUGH_REPORT';

export type ProposalStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'WITHDRAWN';

export type ProposalDecision = 'ACCEPTED' | 'REJECTED';

export interface ProposalAction {
  id: string;
  actionType: string;
  assignedActorType: string;
  assignedActorRefId?: string;
  status: string;
  sequenceOrder: number;
  metadata?: Record<string, unknown>;
}

export interface Proposal {
  id: string;
  brokerageId: string;
  profileId: string;
  targetClientId: string;
  proposalType: ProposalType;
  proposalNumber: string;
  title?: string;
  status: ProposalStatus;
  clientMessage?: string;
  validUntil?: string;
  /** Quote this proposal was built from by the pension wizard; absent for flat-compose proposals. */
  sourceQuoteId?: string;
  /**
   * Wizard resume-step hint (2 = quote options, 3 = SoS builder, 4 = review
   * and send). Only populated on single-proposal reads (`GET /proposals/{id}`),
   * never on list endpoints.
   */
  wizardStep?: number;
  /**
   * True when the proposal was born in the sectioned Pension Proposal
   * Builder v2 (has `proposal_sections` rows). Only populated on
   * single-proposal reads — resume routing sends builder-born proposals to
   * the builder page instead of wizard v1's steps 3/4.
   */
  builder?: boolean;
  sentAt?: string;
  viewedAt?: string;
  decision?: ProposalDecision;
  decisionComment?: string;
  decidedAt?: string;
  createdAt: string;
  updatedAt?: string;
  actions: ProposalAction[];
  details?: Record<string, unknown> | null;
}

/**
 * Step 1 submit of the pension proposal wizard. Mirrors
 * `CreatePensionWizardRequestDTO` on the backend — notably, the client's DOB
 * is intentionally absent (PRD RQ-6): it is read server-side from the target
 * client's profile and is display-only in the wizard.
 */
export interface CreatePensionWizardRequest {
  profileId: string;
  targetClientId: string;
  pensionType: string;
  amount: number;
  currencyCode: string;
  providerIds: string[];
  normalRetirementAge: number;
  specialRequests?: string;
  title?: string;
}

/** Result of bootstrapping the pension proposal wizard. */
export interface PensionWizardBootstrapResult {
  proposalId: string;
  quoteId: string;
  proposal: Proposal;
}

/**
 * Result of bootstrapping any quote-backed proposal builder (Investment &
 * Proposals PRD — same wire shape as {@link PensionWizardBootstrapResult}).
 */
export type QuoteBackedProposalBootstrapResult = PensionWizardBootstrapResult;

/**
 * Step 1 submit of the Investment / Protection builder's Quote Request step.
 * Mirrors `CreateQuoteBackedProposalDTO` on the backend — the client's DOB is
 * intentionally absent (mirrors {@link CreatePensionWizardRequest}'s RQ-6):
 * it is read server-side from the target/secondary client's profile.
 */
export interface CreateQuoteBackedProposalRequest {
  profileId: string;
  targetClientId: string;
  proposalType: 'FUND_INVESTMENT' | 'PROTECTION';
  /** Set for a two-life ("Both") quote — the profile's second client. */
  secondaryClientId?: string;
  amount: number;
  currencyCode: string;
  providerIds: string[];
  specialRequests?: string;
  title?: string;
  investmentType?: string;
  governmentLevyCovered?: boolean;
  protectionType?: string;
  coverType?: string;
  termOfCover?: number;
}

export interface CreateProposalRequest {
  profileId: string;
  targetClientId: string;
  proposalType: ProposalType;
  title?: string;
  clientMessage?: string;
  validUntil?: string;
  details: Record<string, unknown>;
}

export interface UpdateProposalRequest {
  title?: string;
  clientMessage?: string;
  validUntil?: string;
  details: Record<string, unknown>;
}

export const PROPOSAL_TYPE_LABELS: Record<ProposalType, string> = {
  PENSION: 'Pension',
  FUND_INVESTMENT: 'Investment',
  PROTECTION: 'Protection',
  BREAKTHROUGH_REPORT: 'Breakthrough Report',
};

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
};

/** Pension product types offered in the compose modal. */
export const PENSION_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'PRSA_STANDARD', label: 'PRSA (Standard)' },
  { value: 'PERSONAL_PENSION', label: 'Personal Pension' },
  { value: 'EXECUTIVE_PENSION', label: 'Executive Pension' },
  { value: 'PRB_BOB', label: 'PRB / Buy-out Bond' },
  { value: 'ARF', label: 'ARF' },
];

export const CONTRIBUTION_FREQUENCY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SINGLE', label: 'Single' },
];

export const INVESTMENT_STYLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'LUMP_SUM', label: 'Lump sum' },
  { value: 'REGULAR', label: 'Regular' },
];

export const PROTECTION_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MORTGAGE_PROTECTION', label: 'Mortgage Protection' },
  { value: 'TERM_LIFE_INSURANCE', label: 'Term Life Insurance' },
  { value: 'WHOLE_OF_LIFE_INSURANCE', label: 'Whole of Life Insurance' },
  { value: 'SERIOUS_ILLNESS_COVER', label: 'Serious Illness Cover' },
  { value: 'INCOME_PROTECTION', label: 'Income Protection' },
];

export const COVER_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'JOINT', label: 'Joint' },
  { value: 'DUAL', label: 'Dual' },
];

export const PREMIUM_FREQUENCY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUAL', label: 'Annual' },
];

export type ProposalOverviewDisplayStatus =
  | 'DRAFT'
  | 'AWAITING_CLIENT'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'WITHDRAWN';

export interface ProposalOverviewItem {
  proposalId: string;
  profileId: string;
  profileName?: string;
  targetClientName?: string;
  proposalType: string;
  proposalNumber: string;
  title?: string;
  displayStatus: ProposalOverviewDisplayStatus;
  decision?: ProposalDecision;
  lastUpdated: string;
}

export interface ProposalFilterOption {
  value: string;
  label: string;
}

export function proposalTypeLabel(type: string): string {
  return PROPOSAL_TYPE_LABELS[type as ProposalType] ?? type;
}

export function proposalStatusLabel(status: string): string {
  return PROPOSAL_STATUS_LABELS[status as ProposalStatus] ?? status;
}

// ── Statement of Suitability (pension wizard Step 3) ────────────────────

/**
 * Lifecycle of a proposal's Statement of Suitability document. Mirrors
 * `SuitabilityStatementStatus` on the backend. Any section edit after
 * GENERATED reverts the row to DRAFT server-side — the builder surfaces a
 * "regenerate before sending" hint when it observes that transition.
 */
export type SuitabilityStatementStatus = 'DRAFT' | 'GENERATED' | 'SENT' | 'SIGNED';

/** One Statement of Suitability section, mirrors `SosSectionDTO`. */
export interface SosSection {
  sectionKey: string;
  title: string;
  html: string;
}

/** Mirrors `SuitabilityStatementDTO`. */
export interface SuitabilityStatement {
  proposalId: string;
  status: SuitabilityStatementStatus;
  /** The six sections, in canonical order as returned by the backend. */
  sections: SosSection[];
  aiSectionsMetadata?: Record<string, unknown>;
  unsignedDocumentAvailable: boolean;
  signedDocumentAvailable: boolean;
  generatedAt?: string;
  advisorSignatureName?: string;
}

/**
 * Canonical section keys, in wizard order. Only used as a fallback / for
 * membership checks — actual rendering order always follows whatever the
 * backend returns in `SuitabilityStatement.sections`.
 */
export const SOS_SECTION_KEYS = [
  'introduction',
  'needs_objectives',
  'suitability_recommendations',
  'sustainability',
  'precautions',
  'declarations',
] as const;

export type SosSectionKey = (typeof SOS_SECTION_KEYS)[number];

/**
 * The only two sections offered AI drafting (PRD §3.1 Step 3 table) — the
 * remaining four are template-seeded merge-field / boilerplate sections with
 * no AI affordance.
 */
export const SOS_AI_SECTION_KEYS: ReadonlySet<string> = new Set([
  'needs_objectives',
  'suitability_recommendations',
]);

// ── Pension Proposal Builder v2 (sectioned builder) ──────────────────────
// docs/specs/pension-proposal-builder-v2-prd.md §3

/** The stripped PENSION v2 section set, in canonical order (PRD §3.2). */
export const PENSION_BUILDER_SECTION_KEYS = [
  'ABOUT_YOU',
  'NEEDS_OBJECTIVES',
  'PENSION_RECOMMENDATION',
  'CASHFLOW_IMPACT',
  'SUSTAINABILITY',
  'PRECAUTIONS',
  'NEXT_STEPS',
  'DECLARATIONS',
] as const;

/**
 * The Investment/Protection builder section sets (Investment & Protection
 * Proposals PRD RQ-3) — identical to {@link PENSION_BUILDER_SECTION_KEYS}
 * with only the recommendation key swapped.
 */
export const INVESTMENT_BUILDER_SECTION_KEYS = [
  'ABOUT_YOU',
  'NEEDS_OBJECTIVES',
  'INVESTMENT_RECOMMENDATION',
  'CASHFLOW_IMPACT',
  'SUSTAINABILITY',
  'PRECAUTIONS',
  'NEXT_STEPS',
  'DECLARATIONS',
] as const;

export const PROTECTION_BUILDER_SECTION_KEYS = [
  'ABOUT_YOU',
  'NEEDS_OBJECTIVES',
  'PROTECTION_RECOMMENDATION',
  'CASHFLOW_IMPACT',
  'SUSTAINABILITY',
  'PRECAUTIONS',
  'NEXT_STEPS',
  'DECLARATIONS',
] as const;

/** Optional section keys the rail's "+" chooser may add/remove (PRD §3.2). */
export const ADDABLE_PROPOSAL_SECTION_KEYS = ['APPENDICES'] as const;

/**
 * Sections that do NOT gate the Share With Client button (PRD §3.4) — every
 * other loaded section must report `complete: true` before sharing.
 */
export const SHARE_OPTIONAL_SECTION_KEYS: ReadonlySet<string> = new Set([
  'APPENDICES',
  'NEXT_STEPS',
]);

/** One row of `proposal_sections`, mirrors `ProposalSectionDTO`. */
export interface ProposalSection {
  sectionKey: string;
  title: string;
  sortOrder: number;
  complete: boolean;
  content: Record<string, unknown> | null;
  aiMetadata?: Record<string, unknown> | null;
  updatedAt?: string;
}

/** Body of `PUT /api/proposals/{id}/sections/{sectionKey}`. */
export interface UpdateProposalSectionRequest {
  content: Record<string, unknown>;
  title?: string;
}

/** ABOUT_YOU per-client fact row (PRD §3.3). */
export interface ProposalSectionClientFact {
  clientId: string;
  name: string;
  dateOfBirth?: string;
  currentAge?: number;
  maritalStatus?: string;
  taxResidency?: string;
  taxDomicile?: string;
  attitudeToRisk?: string;
}

/** ABOUT_YOU dependant row (PRD §3.3). */
export interface ProposalSectionDependant {
  name: string;
  relationship: string;
  age?: number;
}

/** `content` shape for the ABOUT_YOU section. */
export interface AboutYouSectionContent {
  live?: boolean;
  snapshot?: {
    clients: ProposalSectionClientFact[];
    dependants: ProposalSectionDependant[];
  };
}

/** `content` shape for the NEEDS_OBJECTIVES section. */
export interface NeedsObjectivesSectionContent {
  bullets: string[];
  narrative: string;
}

/** Charge figures of one quote option, denormalised onto the section content. */
export interface RecommendationOptionFigures {
  amount?: number;
  currencyCode?: string;
  allocationPercent?: number;
  amcPercent?: number;
  fundManagementCharges?: number[];
  exitPenalties?: number[];
  clawbacks?: number[];
}

/**
 * SERVER-OWNED summary of a quote option (the selected one, or a pinned
 * comparison alternative) inside the PENSION_RECOMMENDATION content. The
 * server re-derives these from the quote on every save — anything the client
 * sends for them is discarded.
 */
export interface RecommendationOptionSummary {
  quoteId?: string;
  optionId?: string;
  providerId?: string;
  providerName?: string;
  providerLogoUrl?: string;
  figures?: RecommendationOptionFigures;
}

/**
 * One recommended fund allocation inside a PENSION/INVESTMENT recommendation
 * section. `fundKey` is the funds-service catalogue key (undefined for a
 * manually-typed fund when funds-service is unavailable); `fundName` is the
 * required display label; `allocationPercent` is optional (a single-fund
 * recommendation needn't allocate). This is the structured successor to the
 * legacy free-text `recommendedFund` string.
 */
export interface RecommendedFundLine {
  fundKey?: string;
  fundName: string;
  providerName?: string;
  allocationPercent?: number;
}

/** `content` shape for the PENSION_RECOMMENDATION section (PRD §3.3). */
export interface PensionRecommendationSectionContent {
  recommendedAmount?: number;
  /**
   * Legacy single-fund free-text label. Kept for back-compat: still written
   * as a comma-joined summary of {@link recommendedFunds} so older readers
   * keep working, and read as the seed for a single line when
   * {@link recommendedFunds} is absent.
   */
  recommendedFund?: string;
  /** Structured fund allocations selected via the funds-service picker. */
  recommendedFunds?: RecommendedFundLine[];
  rationale?: string;
  /** Advisor toggle — include the provider comparison table; default true. */
  comparisonVisible?: boolean;
  /** Server-set when the quote selection changed under an existing rationale. */
  staleSinceQuoteEdit?: boolean;
  /** SERVER-OWNED, read-only. */
  selected?: RecommendationOptionSummary;
  /** SERVER-OWNED pinned alternatives, read-only. */
  comparison?: RecommendationOptionSummary[];
}

/**
 * `content` shape for the CASHFLOW_IMPACT section. `snapshot` is only set at
 * share time and is ignored by the builder.
 */
export interface CashflowImpactSectionContent {
  scenarioId?: string;
  caption?: string;
  narrative?: string;
  snapshot?: Record<string, unknown>;
}

/** A single rich-text block within a `{blocks: [...]}` section content. */
export interface ProposalSectionRichTextBlock {
  title?: string;
  html: string;
}

/**
 * `content` shape shared by SUSTAINABILITY / PRECAUTIONS / NEXT_STEPS /
 * DECLARATIONS / APPENDICES — single-block in practice this phase.
 */
export interface RichTextSectionContent {
  blocks: ProposalSectionRichTextBlock[];
}

// ── Breakthrough Report Builder (docs/specs/breakthrough-report-prd.md §4) ──

/**
 * The full BREAKTHROUGH_REPORT default section set — every one of these is
 * required-for-send (mirrors `BreakthroughReportHandler.sectionSet()`; only
 * the addable `BR_APPENDICES` is exempt).
 */
export const BREAKTHROUGH_BUILDER_SECTION_KEYS = [
  'BR_COVER',
  'BR_ABOUT_YOU',
  'BR_UNDERSTANDING',
  'BR_GOALS',
  'BR_CASH_POSITION',
  'BR_LIFESTYLE_RETIREMENT',
  'BR_GIVING_LEGACY',
  'BR_INVESTMENT_STRATEGY',
  'BR_PENSION_OPTIONS',
  'BR_TAX',
  'BR_FUNDS',
  'BR_MARKET_INSIGHTS',
  'BR_NEXT_STEPS',
  'BR_DISCLAIMER',
] as const;

/** Optional section keys the Breakthrough Report rail's "+" chooser may add/remove. */
export const BREAKTHROUGH_ADDABLE_SECTION_KEYS = ['BR_APPENDICES'] as const;

/**
 * Every default-created Breakthrough Report section is required-for-send
 * except the addable Appendices (PRD §4 — unlike PENSION there is no
 * `defaulted`/exempt section here).
 */
export const BREAKTHROUGH_SHARE_OPTIONAL_SECTION_KEYS: ReadonlySet<string> = new Set([
  'BR_APPENDICES',
]);

/**
 * Authored sections wired to the `REPORT_BUILDER` intelligence use case's
 * "Generate with AI" affordance (PRD §11 RQ-BR7, BR-P6). The value is the
 * section's name as used in the REPORT_BUILDER system prompt (V62) — woven
 * into the steering message so the assistant drafts the right block. Every
 * one of these persists through the generic `html` block
 * (`applyAiBlock(proposalId, sectionKey, 'html', {html}, sessionId)`),
 * matching the rich-text `{blocks:[{html}]}` shape `SectionRichText` already
 * renders/saves. Sections not listed here (server-owned figures, BR_COVER,
 * BR_ABOUT_YOU, and the optional BR_INVESTMENT_STRATEGY / BR_DISCLAIMER /
 * BR_APPENDICES) get no AI row.
 */
export const BREAKTHROUGH_AI_SECTION_LABELS: Readonly<Record<string, string>> = {
  BR_UNDERSTANDING: 'our-understanding-of-you',
  BR_GOALS: 'goals / wealth-allocation framing',
  BR_GIVING_LEGACY: 'giving & legacy',
  BR_MARKET_INSIGHTS: 'market insights',
  BR_NEXT_STEPS: 'next steps',
};

/**
 * Report-level fields not tied to a single section — mirrors the backend's
 * `BreakthroughProposalInput` (write) / `BreakthroughProposalView` (read),
 * both surfaced as `Proposal.details`. Every field is optional.
 */
export interface BreakthroughProposalDetails {
  linkedQuoteId?: string;
  cashflowScenarioIds?: string[];
  recommendedFundKeys?: string[];
  reportIntro?: string;
  reportTheme?: string;
}

/**
 * `content` shape for BR_COVER (PRD §4 row 1) — meta fields seeded entirely
 * at builder creation, plus an advisor-editable `reportIntro` persisted
 * verbatim on the section (there is no server resolver for this key).
 */
export interface BreakthroughCoverSectionContent {
  title?: string;
  clientName?: string;
  advisorName?: string;
  brokerageName?: string;
  date?: string;
  reportIntro?: string;
}

/** One BR_CASH_POSITION account row, server-projected from Financial Position. */
export interface BreakthroughCashPositionAccount {
  accountId?: string;
  owner?: string;
  institution?: string;
  type?: string;
  label?: string;
  balance?: number;
  currency?: string;
}

/** `content` shape for BR_CASH_POSITION (server-owned; PRD §4 row 5). */
export interface BreakthroughCashPositionSectionContent {
  accounts?: BreakthroughCashPositionAccount[];
  totalCash?: number;
  currencyCode?: string;
  /** Advisor acknowledges the client genuinely has no cash accounts to report. */
  noAccountsAcknowledged?: boolean;
}

/** One frozen income-source stacked series for an embedded cashflow scenario. */
export interface BreakthroughLifestyleScenarioSeries {
  scenarioId: string;
  name: string;
  labels: number[];
  series: Array<{ name: string; values: number[] }>;
}

export interface BreakthroughLifestylePensionOwnerRow {
  owner?: string;
  value?: number;
  policyCount?: number;
}

export interface BreakthroughLifestylePensionSummary {
  total?: number;
  policyCount?: number;
  perOwner?: BreakthroughLifestylePensionOwnerRow[];
}

/**
 * `content` shape for BR_LIFESTYLE_RETIREMENT (PRD §4 row 6 / RQ-BR4) — the
 * advisor picks 1..N saved cashflow scenarios (persisted on
 * `BreakthroughProposalDetails.cashflowScenarioIds`); `scenarios` is
 * server-derived from that list on every read while DRAFT.
 */
export interface BreakthroughLifestyleSectionContent {
  scenarios?: BreakthroughLifestyleScenarioSeries[];
  pensionSummary?: BreakthroughLifestylePensionSummary;
  narrative?: string;
}

/** One BR_PENSION_OPTIONS Financial Position pension holding row. */
export interface BreakthroughPensionHoldingRow {
  policyId?: string;
  label?: string;
  owner?: string;
  value?: number;
}

/**
 * `content` shape for BR_PENSION_OPTIONS (PRD §4 row 9 / RQ-BR5) — an
 * optional `BreakthroughProposalDetails.linkedQuoteId` supplies `selected`/
 * `comparison`; when absent, `pensions` (Financial Position holdings) is the
 * fallback. `recommendedFund`/`rationale` are advisor-authored.
 */
export interface BreakthroughPensionOptionsSectionContent {
  selected?: RecommendationOptionSummary;
  comparison?: RecommendationOptionSummary[];
  pensions?: BreakthroughPensionHoldingRow[];
  recommendedFund?: string;
  rationale?: string;
}

/** One BR_TAX server-derived context note. */
export interface BreakthroughTaxNoteRow {
  label?: string;
  value?: number;
  currencyCode?: string;
}

/** `content` shape for BR_TAX (PRD §4 row 10). */
export interface BreakthroughTaxSectionContent {
  notes?: BreakthroughTaxNoteRow[];
  commentary?: string;
}
